import type { SecondaryDimensionValues as CoreSecondaryDimensionValues } from '@revelio/core';
import type {
  CompositionV3RespData,
  TransitionsBreakdowns,
} from '@revelio/data-access';

// Import base type from core and re-export for backward compatibility
export type SecondaryDimensionValues = CoreSecondaryDimensionValues;

export const COLUMN_LEVELS = {
  role: ['role_lvl_1', 'role_lvl_3', 'role_lvl_5', 'role_lvl_7'],
  skills: ['skill_lvl_3', 'skill_lvl_5', 'skill_lvl_6', 'skill_lvl_7'],
  activities: ['activity_lvl_3', 'activity_lvl_5', 'activity_lvl_6'],
  rics: ['rics_k10', 'rics_k400'],
  geography: ['region', 'country', 'state', 'msa', 'county', 'city'],
} as const satisfies Partial<
  Record<SecondaryDimensionValues, readonly string[]>
>;

type ColumnTaxonomy = keyof typeof COLUMN_LEVELS;

export type ColumnLevel = (typeof COLUMN_LEVELS)[ColumnTaxonomy][number];

export type ColumnDimension = SecondaryDimensionValues | ColumnLevel;

const TAXONOMY_OF_COLUMN_LEVEL: ReadonlyMap<ColumnDimension, ColumnTaxonomy> =
  new Map(
    (Object.keys(COLUMN_LEVELS) as ColumnTaxonomy[]).flatMap((taxonomy) =>
      COLUMN_LEVELS[taxonomy].map((level) => [level, taxonomy] as const)
    )
  );

export const isColumnLevel = (dimension: string): dimension is ColumnLevel =>
  TAXONOMY_OF_COLUMN_LEVEL.has(dimension as ColumnDimension);

export const columnTaxonomyOf = (
  dimension: ColumnDimension
): SecondaryDimensionValues =>
  isColumnLevel(dimension)
    ? (TAXONOMY_OF_COLUMN_LEVEL.get(dimension) ?? 'geography')
    : dimension;

export const COLUMN_LEVEL_LABELS: Readonly<Record<ColumnLevel, string>> = {
  role_lvl_1: 'Job Category',
  role_lvl_3: 'Role k150',
  role_lvl_5: 'Role k1500',
  role_lvl_7: 'Role k15000',
  skill_lvl_3: 'Skill Level 3',
  skill_lvl_5: 'Skill Level 5',
  skill_lvl_6: 'Skill Level 6',
  skill_lvl_7: 'Skill Level 7',
  activity_lvl_3: 'Activity k150',
  activity_lvl_5: 'Activity k1500',
  activity_lvl_6: 'Activity k5000',
  rics_k10: 'Industry k10',
  rics_k400: 'Industry k400',
  region: 'Region',
  country: 'Country',
  state: 'State',
  msa: 'Metro Area',
  county: 'County',
  city: 'City',
};

export type CompositionSecondaryDimensionValues = keyof Pick<
  CompositionV3RespData,
  | 'rics'
  | 'geography'
  | 'role'
  | 'skills'
  | 'gender'
  | 'ethnicity'
  | 'education'
  | 'seniority'
>;

const compositionSecondaryDimension: Record<
  CompositionSecondaryDimensionValues,
  boolean
> = {
  rics: true,
  geography: true,
  role: true,
  skills: true,
  gender: true,
  ethnicity: true,
  education: true,
  seniority: true,
};
export const isCompositionSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is CompositionSecondaryDimensionValues =>
  compositionSecondaryDimension[segment as CompositionSecondaryDimensionValues];

export const compositionSecondaryDimensionSet: ReadonlySet<string> = new Set(
  Object.keys(compositionSecondaryDimension)
);

export type TransitionSecondaryDimensionValues = keyof Pick<
  TransitionsBreakdowns,
  | 'role'
  | 'rics'
  | 'geography'
  | 'seniority'
  | 'gender'
  | 'ethnicity'
  | 'education'
>;

const transitionSecondaryDimension: Record<
  TransitionSecondaryDimensionValues,
  boolean
> = {
  role: true,
  rics: true,
  geography: true,
  gender: true,
  ethnicity: true,
  education: true,
  seniority: true,
};
export const isTransitionSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is TransitionSecondaryDimensionValues =>
  transitionSecondaryDimension[segment as TransitionSecondaryDimensionValues];

export type PostingSecondaryDimension = 'role' | 'company' | 'timeseries';

const postingSecondaryDimension: Record<PostingSecondaryDimension, boolean> = {
  role: true,
  company: true,
  timeseries: true,
};
export const isPostingSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is PostingSecondaryDimension =>
  postingSecondaryDimension[segment as PostingSecondaryDimension];

export type CompensationSecondaryDimension = 'role' | 'company' | 'geography';

const compensationSecondaryDimension: Record<
  CompensationSecondaryDimension,
  boolean
> = {
  role: true,
  company: true,
  geography: true,
};
export const isCompensationSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is CompensationSecondaryDimension =>
  compensationSecondaryDimension[segment as CompensationSecondaryDimension];

export type { SecondaryDimensionOption } from '@revelio/core/dashboard-capabilities';
export { secondaryDimensionOptions } from '@revelio/core/dashboard-capabilities';
